import { AuthenticatedHttp } from '@/http/axios';
import { ACCOUNT_RELATIONSHIP } from '@/consts/accounts';
import { BUSINESS_DOCUMENT_TYPE } from '@/consts/businesses';
import { normalizeBusiness } from '@/services/businesses';

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const resolveDocumentType = (raw) =>
  String(pickFirst(raw.document_type, raw.documentType) ?? '').toLowerCase() ===
  BUSINESS_DOCUMENT_TYPE.CNPJ
    ? BUSINESS_DOCUMENT_TYPE.CNPJ
    : BUSINESS_DOCUMENT_TYPE.CPF;

const normalizeAccountBusiness = (raw = {}) => ({
  ...normalizeBusiness(raw),
  documentFormatted: pickFirst(raw.document_formatted, raw.documentFormatted) ?? '',
  ownerName: pickFirst(raw.owner_name, raw.ownerName) ?? '',
});

export const normalizeAccount = (raw = {}) => {
  const documentType = resolveDocumentType(raw);
  const documentFormatted = pickFirst(raw.document_formatted, raw.documentFormatted) ?? '';
  const relationship =
    pickFirst(raw.relationship) === ACCOUNT_RELATIONSHIP.OWNER
      ? ACCOUNT_RELATIONSHIP.OWNER
      : ACCOUNT_RELATIONSHIP.SHARED;

  return {
    ownerId: String(pickFirst(raw.owner_id, raw.ownerId) ?? ''),
    name: pickFirst(raw.name) ?? '',
    documentType,
    isCompany: documentType === BUSINESS_DOCUMENT_TYPE.CNPJ,
    documentFormatted,
    documentLabel: documentFormatted ? `${documentType.toUpperCase()} ${documentFormatted}` : '',
    email: pickFirst(raw.email) ?? '',
    phone: pickFirst(raw.phone) ?? '',
    relationship,
    isOwn: relationship === ACCOUNT_RELATIONSHIP.OWNER,
    businesses: (Array.isArray(raw.businesses) ? raw.businesses : []).map(normalizeAccountBusiness),
  };
};

// Enquanto o backend não liga a rota, ela responde 403 ou 404, e isso não é falha:
// o painel segue com o seletor de negócio e a troca de conta no menu de perfil.
const FEATURE_UNAVAILABLE_STATUS = [403, 404];

// Sem o header da impersonação: a lista é de quem está logado, não da conta em uso,
// e precisa responder igual de dentro de qualquer conta.
export const getAccessibleAccounts = async () => {
  try {
    const { data } = await AuthenticatedHttp.get('/businesses/accounts/', {
      skipImpersonation: true,
    });
    const list = Array.isArray(data) ? data : data?.results;

    return (list ?? []).map(normalizeAccount);
  } catch (error) {
    if (FEATURE_UNAVAILABLE_STATUS.includes(error?.response?.status)) {
      return null;
    }

    throw error;
  }
};

// Com o header da impersonação: os dados são da conta em uso, e o vínculo vem do backend.
export const getAccountDetails = async () => {
  const { data } = await AuthenticatedHttp.get('/businesses/account-details/');

  return normalizeAccount(data);
};
